import React, { useState } from 'react';
import moment from 'moment';
import { useQuery } from '@apollo/client';
import ArticleIcon from '@mui/icons-material/Article';
import {
  Badge,
  Tooltip
} from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';
import { articleQueries } from '../../../../../graphql/queries';
import {
  getArticleByDate,
  getArticlesForMonth,
  getCalendarBackNavFloor
} from '../../../../../helpers/subscription';

function ArticleDay (props) {
  const { articleDays = [], day, articles, outsideCurrentMonth, ...other } = props;

  const isSelected =
    !outsideCurrentMonth && articleDays.indexOf(day.date()) >= 0;

  // The day's article (badge tooltip + nothing else) — the month feed carries publicId
  // and title per day, so the click target and the hover title come from the same rows.
  const article = getArticleByDate(articles, day.format('YYYY-MM-DD'));

  if (!article) {
    return <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} disabled day={day} />;
  }

  return (
    <Tooltip
      placement='top-end'
      title={article.title}
      arrow
    >
      <Badge
        key={day.toString()}
        overlap='circular'
        badgeContent={
          isSelected
            ? <ArticleIcon color='success' fontSize='small' sx={{ height: '.7em' }} />
            : undefined
          }
      >
        <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} data-testid={isSelected ? 'calendar-article-day' : undefined} />
      </Badge>
    </Tooltip>
  );
}

// The customer paid Calendar fetches ONE month at a time (articlesByMonthAndSubscription),
// re-fetching as the reader navigates — it never reuses the List's recency chunks, which
// would have no data for an older month. A clicked day reuses the List's
// navigate-by-publicId path (onArticleClick → the detail pane's latestArticleOrByPublicId
// fetch), so the calendar needs no separate by-date query. Back-nav is floored at the
// subscription's first-article month via subscriptionFirstArticleDate + getCalendarBackNavFloor.
//
// `subscriptionId` present ⇒ paid path (bounded month queries). Absent ⇒ free path (LBD-3):
// bucket the small `availableArticles` client array with the same helpers, no bounded query.
export default function ArticleCalendar ({ subscriptionId, availableArticles = [], onArticleClick, selectedArticleDate }) {
  const isPaid = !!subscriptionId;
  // Open on the shown article's month (deep-linked or latest), with its day selected —
  // not on today. The calendar mounts on view-switch, by which point the article is
  // loaded, so a lazy useState initialiser is enough (no effect/sync needed).
  const [visibleDate, setVisibleDate] = useState(() => selectedArticleDate ? moment(selectedArticleDate) : moment());
  const [selectedDayValue, setSelectedDayValue] = useState(() => selectedArticleDate ? moment(selectedArticleDate) : null);

  const { data: monthData, loading: monthLoading } = useQuery(
    articleQueries.ArticlesByMonthAndSubscription,
    {
      variables: {
        subscriptionId,
        year: visibleDate.year(),
        month: visibleDate.month() + 1 // grid month is 0-indexed; the resolver takes 1-12
      },
      skip: !isPaid
    }
  );

  const { data: floorData } = useQuery(
    articleQueries.SubscriptionFirstArticleDate,
    {
      variables: { subscriptionId },
      skip: !isPaid
    }
  );

  // paid: the visible month's articles; free: the small client array (LBD-3 branch)
  const monthArticles = isPaid
    ? ((monthData && monthData.articlesByMonthAndSubscription) || [])
    : availableArticles;

  const minDate = isPaid && floorData
    ? getCalendarBackNavFloor(floorData.subscriptionFirstArticleDate)
    : null;

  const articleDays = getArticlesForMonth(monthArticles, visibleDate.month(), visibleDate.year());

  const handleDayChange = (value) => {
    setSelectedDayValue(value);
    const article = getArticleByDate(monthArticles, value.format('YYYY-MM-DD'));
    if (article) {
      onArticleClick({
        publicId: article.publicId,
        title: article.title
      });
    }
  };

  // Navigating months re-points visibleDate, which re-runs the month query for that month.
  // Keep MUI's moment as-is (read year/month in its own frame) — converting to UTC here
  // would shift the month label across the boundary for readers ahead of UTC.
  const handleMonthChange = (date) => {
    setVisibleDate(date);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DateCalendar
        value={selectedDayValue}
        onChange={handleDayChange}
        onMonthChange={handleMonthChange}
        minDate={minDate || undefined}
        loading={monthLoading}
        renderLoading={() => <DayCalendarSkeleton />}
        slots={{
          day: ArticleDay
        }}
        slotProps={{
          day: {
            articleDays,
            articles: monthArticles
          }
        }}
      />
    </LocalizationProvider>
  );
}
